import type { components } from "../../../types/api";
import { assertReportAdmission } from "../nativeConditions.mjs";
import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import { asRecord, DesignerDocument, JsonRecord } from "./reportDesignerModel";

export type ReportDefinitionSummaryV3 = components["schemas"]["ReportDefinitionSummaryV3"];
export type ReportDefinitionDetailV3 = components["schemas"]["ReportDefinitionDetailV3"];
export type ReportDefinitionUpsertRequestV3 = components["schemas"]["ReportDefinitionUpsertRequestV3"];
export type ReportDefinitionValidateResponseV3 = components["schemas"]["ReportDefinitionValidateResponseV3"];
export type ReportDefinitionValidateRequestV3 = components["schemas"]["ReportDefinitionValidateRequestV3"];

export const DEFINITION_MANAGEMENT_OPTIONS = { query: { includeInactive: "true" } };

export function detailToLegacyPayload(detail: ReportDefinitionDetailV3): FormattedInvoicePayload {
  assertReportAdmission(detail, "import");
  return {
    definitionId: detail.definitionId,
    metadata: {
      reportName: detail.name || detail.definitionId || "Untitled report",
    },
    assets: {},
    datasets: {},
    layout: detail.layout || {},
    definitionVersion: detail.version,
    schemaVersion: detail.schemaVersion,
    reportKey: detail.reportKey,
    reportType: detail.reportType,
    scope: detail.scope,
    overrideOf: detail.overrideOf,
    name: detail.name,
    requiredFeature: detail.requiredFeature,
    customFeature: detail.customFeature,
    roles: detail.roles || [],
    requiredFilters: asRecord(detail.requiredFilters),
    capabilities: asRecord(detail.capabilities),
    data: detail.data,
    isActive: detail.isActive,
  } as FormattedInvoicePayload;
}

export function legacyPayloadToUpsertRequest(payload: FormattedInvoicePayload, document: DesignerDocument, revision?: string | null): ReportDefinitionUpsertRequestV3 {
  assertReportAdmission(payload, "save");
  assertReportAdmission(document, "save");
  const record = payload as FormattedInvoicePayload & JsonRecord;
  return {
    definitionId: payload.definitionId || document.definitionId || undefined,
    name: stringOrUndefined(asRecord(payload.metadata).reportName) || stringOrUndefined(record.name) || payload.definitionId || "Untitled report",
    reportType: stringOrUndefined(record.reportType || document.reportType),
    reportKey: stringOrUndefined(record.reportKey || document.reportKey),
    overrideOf: stringOrUndefined(record.overrideOf || document.overrideOf),
    schemaVersion: stringOrUndefined(record.schemaVersion || document.sourceSchemaVersion) || "reports-v3.0",
    version: numberOrZero(record.definitionVersion ?? document.definitionVersion ?? 1),
    ...(revision ? { revision } : {}),
    requiredFeature: stringOrUndefined(record.requiredFeature || document.renderer.topLevel.requiredFeature),
    customFeature: stringOrUndefined(record.customFeature || document.renderer.topLevel.customFeature),
    roles: Array.isArray(record.roles) ? record.roles.map(String) : Array.isArray(document.roles) ? document.roles.map(String) : [],
    requiredFilters: record.requiredFilters ?? document.requiredFilters ?? {},
    capabilities: record.capabilities ?? document.capabilities ?? {},
    data: record.data !== undefined ? record.data : document.data !== undefined ? document.data : {},
    layout: payload.layout ?? {},
    isActive: typeof record.isActive === "boolean" ? record.isActive : true,
  };
}

export function definitionValidationRequest(request: ReportDefinitionUpsertRequestV3): ReportDefinitionValidateRequestV3 {
  const { revision: _revision, isActive: _isActive, ...validation } = request;
  return validation;
}

export function readDefinitionValidationResponse(value: unknown): { isValid: boolean; errors: string[]; warnings: string[] } {
  const response = asRecord(value);
  if (typeof response.isValid !== "boolean") throw new Error("The API returned an invalid report validation response.");
  const diagnostics = (key: "errors" | "warnings"): string[] => {
    const messages = response[key];
    if (messages == null) return [];
    if (!Array.isArray(messages) || !messages.every((message): message is string => typeof message === "string")) {
      throw new Error(`The API returned invalid report validation ${key}.`);
    }
    return messages;
  };
  const errors = diagnostics("errors");
  const warnings = diagnostics("warnings");
  if (response.isValid && errors.length) throw new Error("The API returned contradictory report validation results.");
  return { isValid: response.isValid, errors, warnings };
}

export function isReportDefinitionConflict(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return "status" in error && error.status === 409 && "code" in error && error.code === "REPORT_DEFINITION_CONFLICT";
}

export function definitionDisplayName(definition: ReportDefinitionSummaryV3 | ReportDefinitionDetailV3): string {
  return definition.name || definition.definitionId || "Untitled definition";
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function numberOrZero(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}
